import { CONTACT_API } from 'modules/contacts/api/contact-api'
import { ContactFilterConditionInterface } from 'modules/contacts/types/contact-filter-interface'
import { ContactApiInterface } from 'modules/contacts/types/contact-interface'
import { useCallback } from 'react'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import { CountInterface, SwrPaginationInterface } from 'shared/types/pagination-interface'
import { baseFetcher } from 'shared/utils/http-client'

interface UseContactsInterface extends SwrPaginationInterface {
  conditions: ContactFilterConditionInterface[]
}

export const CONTACT_LIST_API = `${CONTACT_API}/contacts/list`
export const CONTACT_COUNT_API = `${CONTACT_API}/contacts/count`

export function useContacts({ limit, pagination, conditions }: UseContactsInterface) {
  const { user } = useUser()
  const { fetcher } = useFetch<RequestMethodsEnum.get, ContactApiInterface>({
    method: RequestMethodsEnum.get,
  })

  const filterApi = conditions
    .map((el, idx) => {
      return Object.keys(el)
        .map(
          e =>
            `&filter[conditions][${idx}][${e}]=${encodeURIComponent(
              el[e as keyof ContactFilterConditionInterface] as string,
            )}`,
        )
        .join('')
    })
    .join('')

  const {
    data: contacts,
    mutate,
    abort: abortContactList,
  } = useControlledSwr<ContactApiInterface>(() => {
    if (user && pagination.startFromId === undefined && limit) {
      return [
        `${CONTACT_LIST_API}?pagination[limit]=${limit}${filterApi}`,
        RequestModuleEnum.Tags,
        RequestModuleEnum.Contacts,
      ]
    } else if (user && pagination.startFromId && limit) {
      return [
        `${CONTACT_LIST_API}?pagination[startFrom]=${pagination.startFromId}&pagination[order]=${pagination.order}&pagination[limit]=${limit}${filterApi}`,
        RequestModuleEnum.Tags,
        RequestModuleEnum.Contacts,
      ]
    }
    return null
  }, fetcher)

  const { data: counter, abort: abortCounter } = useControlledSwr<CountInterface>(() => {
    if (user) {
      return [`${CONTACT_COUNT_API}?pagination[limit]${filterApi}`, RequestModuleEnum.Contacts]
    }
    return null
  }, baseFetcher)

  const abortContacts = useCallback(() => {
    abortContactList()
    abortCounter()
  }, [abortContactList, abortCounter])

  return { contacts, mutate, counter, abortContacts }
}
